import { useEffect, useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../convex/_generated/api';
import { Id } from '../convex/_generated/dataModel';

// Cookie utilities
const setCookie = (name: string, value: string, days: number) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
};

// Generate unique IDs
const generateVisitorId = (): string => {
  return `v_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
};

const generateSessionId = (): string => {
  return `s_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
};

// UTM parameter type
export interface UTMParams {
  utm_id?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  // Additional tracking
  referrer?: string;
  landingPage?: string;
}

// Visitor tracking data
export interface VisitorData {
  visitorId: string;
  sessionId: string;
  utmParams: UTMParams;
  isDirectTraffic: boolean;
}

export const useUTMTracking = (projectId?: Id<"projects">) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [visitorData, setVisitorData] = useState<VisitorData | null>(null);
  
  const trackVisit = useMutation(api.visitorTracking.trackVisit);

  useEffect(() => {
    // Get or create visitor ID (30-day cookie)
    let visitorId = getCookie('visitor_id');
    if (!visitorId) {
      visitorId = generateVisitorId();
      setCookie('visitor_id', visitorId, 30);
    }

    // Get or create session ID (expires when browser closes)
    let sessionId = sessionStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = generateSessionId();
      sessionStorage.setItem('session_id', sessionId);
    }

    // Extract UTM parameters from URL
    const currentUTMParams: UTMParams = {
      utm_id: searchParams.get('utm_id') || undefined,
      utm_source: searchParams.get('utm_source') || undefined,
      utm_medium: searchParams.get('utm_medium') || undefined,
      utm_campaign: searchParams.get('utm_campaign') || undefined,
      utm_content: searchParams.get('utm_content') || undefined,
      utm_term: searchParams.get('utm_term') || undefined,
      referrer: document.referrer || undefined,
      landingPage: window.location.href,
    };

    // Check if we have any UTM parameters
    const hasUTMParams = Object.keys(currentUTMParams).some(
      key => key.startsWith('utm_') && currentUTMParams[key as keyof UTMParams]
    );

    // Get stored UTM params from cookie (1 month persistence)
    const storedUTMString = getCookie('utm_params');
    let storedUTMParams: UTMParams = {};
    
    if (storedUTMString) {
      try {
        storedUTMParams = JSON.parse(decodeURIComponent(storedUTMString));
      } catch (e) {

      }
    }

    // If we have new UTM params, update the cookie
    if (hasUTMParams) {
      const mergedUTMParams = { ...storedUTMParams, ...currentUTMParams };
      setCookie('utm_params', encodeURIComponent(JSON.stringify(mergedUTMParams)), 30);
      storedUTMParams = mergedUTMParams;
    }

    // Determine if this is direct traffic
    const isDirectTraffic = !hasUTMParams && !storedUTMParams.utm_source && !document.referrer;

    // Set visitor data
    const data: VisitorData = {
      visitorId,
      sessionId,
      utmParams: hasUTMParams ? currentUTMParams : storedUTMParams,
      isDirectTraffic,
    };
    
    setVisitorData(data);

    // Track the visit if we have a project ID
    if (projectId) {
      const trackVisitAsync = async () => {
        try {
          const ipAddress = await fetch('https://api.ipify.org?format=json')
            .then(res => res.json())
            .then(data => data.ip)
            .catch(() => null);

          await trackVisit({
            visitorId,
            projectId,
            sessionId,
            utmId: data.utmParams.utm_id,
            utmSource: data.utmParams.utm_source || (isDirectTraffic ? 'direct' : undefined),
            utmMedium: data.utmParams.utm_medium,
            utmCampaign: data.utmParams.utm_campaign,
            utmContent: data.utmParams.utm_content,
            utmTerm: data.utmParams.utm_term,
            landingPage: location.pathname,
            referrer: document.referrer || undefined,
            ipAddress,
            userAgent: navigator.userAgent,
            language: navigator.language,
          });
        } catch (error) {
  
        }
      };

      trackVisitAsync();
    }
  }, [searchParams, location, projectId, trackVisit]);

  // Function to get UTM params for lead submission
  const getLeadTrackingData = () => {
    if (!visitorData) return {};

    return {
      visitorId: visitorData.visitorId,
      sessionId: visitorData.sessionId,
      utmId: visitorData.utmParams.utm_id,
      utmSource: visitorData.utmParams.utm_source || (visitorData.isDirectTraffic ? 'direct' : undefined),
      utmMedium: visitorData.utmParams.utm_medium || (visitorData.isDirectTraffic ? 'none' : undefined),
      utmCampaign: visitorData.utmParams.utm_campaign,
      utmContent: visitorData.utmParams.utm_content,
      utmTerm: visitorData.utmParams.utm_term,
    };
  };

  // Function to clear stored UTM params (useful for testing)
  const clearUTMParams = () => {
    document.cookie = 'utm_params=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/';
  };

  return {
    visitorData,
    getLeadTrackingData,
    clearUTMParams,
  };
}; 